import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format, parseISO, getISOWeek, getISOWeekYear, startOfISOWeek, endOfISOWeek, eachDayOfInterval, addDays } from 'date-fns';
import { getDealSalesAmount } from '@/utils/salesAmount';

interface ReactivationProjection {
  date: string;
  count: number;
  isWeekend?: boolean;
  dayOfWeek?: string;
}

interface WeeklyProjection {
  weekKey: string; // yyyy-Www format
  weekLabel: string;
  count: number;
  startDate: string;
  endDate: string;
}

interface ReactivationProjectionData {
  monthly: ReactivationProjection[];
  weekly: WeeklyProjection[];
  daily: ReactivationProjection[];
  totalScheduled: number;
  byStatus: {
    scheduled: number;
    in_progress: number;
    completed: number;
    no_response: number;
  };
}

export function useReactivationProjection() {
  return useQuery({
    queryKey: ['reactivation-projection'],
    queryFn: async (): Promise<ReactivationProjectionData> => {
      // Fetch all contacts with reactivation scheduled
      const { data: contacts, error } = await supabase
        .from('contacts')
        .select('id, next_reactivation_at, reactivation_status')
        .not('next_reactivation_at', 'is', null)
        .order('next_reactivation_at', { ascending: true });

      if (error) throw error;

      const scheduledContacts = contacts || [];
      
      // Count by status
      const { data: statusCounts, error: statusError } = await supabase
        .from('contacts')
        .select('reactivation_status')
        .not('reactivation_status', 'is', null);

      if (statusError) throw statusError;

      const byStatus = {
        scheduled: 0,
        in_progress: 0,
        completed: 0,
        no_response: 0,
      };

      (statusCounts || []).forEach(c => {
        const status = c.reactivation_status as keyof typeof byStatus;
        if (status && byStatus.hasOwnProperty(status)) {
          byStatus[status]++;
        }
      });

      // Group by month, week, and day
      const monthlyMap = new Map<string, number>();
      const weeklyMap = new Map<string, { count: number; startDate: Date; endDate: Date }>();
      const dailyMap = new Map<string, number>();

      scheduledContacts.forEach(contact => {
        if (contact.next_reactivation_at) {
          // Extract date string directly to avoid timezone conversion issues
          // next_reactivation_at comes as "2025-12-19" or "2025-12-19T00:00:00+00"
          const dateStr = contact.next_reactivation_at.split('T')[0].split(' ')[0];
          const dayKey = dateStr; // yyyy-MM-dd format
          const monthKey = dateStr.substring(0, 7); // yyyy-MM format
          
          // For week calculations, parse the date at noon UTC to avoid timezone shifts
          const [year, month, day] = dateStr.split('-').map(Number);
          const date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
          
          // ISO week format: yyyy-Www
          const weekYear = getISOWeekYear(date);
          const weekNum = getISOWeek(date);
          const weekKey = `${weekYear}-W${String(weekNum).padStart(2, '0')}`;
          const weekStart = startOfISOWeek(date);
          const weekEnd = endOfISOWeek(date);

          monthlyMap.set(monthKey, (monthlyMap.get(monthKey) || 0) + 1);
          dailyMap.set(dayKey, (dailyMap.get(dayKey) || 0) + 1);
          
          const existing = weeklyMap.get(weekKey);
          if (existing) {
            existing.count++;
          } else {
            weeklyMap.set(weekKey, { count: 1, startDate: weekStart, endDate: weekEnd });
          }
        }
      });

      // Convert to sorted arrays
      const monthly = Array.from(monthlyMap.entries())
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => a.date.localeCompare(b.date));

      const weekly: WeeklyProjection[] = Array.from(weeklyMap.entries())
        .map(([weekKey, data]) => ({
          weekKey,
          weekLabel: `Sem ${weekKey.split('-W')[1]} - ${format(data.startDate, 'dd/MM')} a ${format(data.endDate, 'dd/MM')}`,
          count: data.count,
          startDate: format(data.startDate, 'yyyy-MM-dd'),
          endDate: format(data.endDate, 'yyyy-MM-dd'),
        }))
        .sort((a, b) => a.weekKey.localeCompare(b.weekKey));

      const daily = Array.from(dailyMap.entries())
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => a.date.localeCompare(b.date));

      return {
        monthly,
        weekly,
        daily,
        totalScheduled: scheduledContacts.length,
        byStatus,
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

interface SourceChannelData {
  total: number;
  won: number;
  totalValue: number;
  wonValue: number;
}

interface SourceChannelEntry extends SourceChannelData {
  name: string;
  conversionRate: number;
  avgValue: number;
}

export function useDealsSourceChannel(deals: any[]) {
  // Process deals by source - usar valor efetivo (regra de cancelamento)
  const bySource = deals.reduce<Record<string, SourceChannelData>>((acc, deal) => {
    const source = deal.deals_source || 'Unknown';
    if (!acc[source]) {
      acc[source] = { total: 0, won: 0, totalValue: 0, wonValue: 0 };
    }
    
    // Calcular valor efetivo aplicando regra de cancelamento + upsell
    const effectiveAmount = getDealSalesAmount({
      deal_status: deal.deal_status,
      closed_date: deal.closed_date,
      cancelled_at: deal.cancelled_at,
      total_amount: deal.total_amount,
      amount_received: deal.amount_received || 0,
      upsell_credit_amount: deal.upsell_credit_amount || 0,
    });
    
    acc[source].total++;
    acc[source].totalValue += effectiveAmount;
    if (deal.stage === 'WON') {
      acc[source].won++;
      acc[source].wonValue += effectiveAmount;
    }
    return acc;
  }, {});

  // Process deals by channel - usar valor efetivo
  const byChannel = deals.reduce<Record<string, SourceChannelData>>((acc, deal) => {
    const channel = deal.deals_channel || 'Unknown';
    if (!acc[channel]) {
      acc[channel] = { total: 0, won: 0, totalValue: 0, wonValue: 0 };
    }
    
    // Calcular valor efetivo aplicando regra de cancelamento + upsell
    const effectiveAmount = getDealSalesAmount({
      deal_status: deal.deal_status,
      closed_date: deal.closed_date,
      cancelled_at: deal.cancelled_at,
      total_amount: deal.total_amount,
      amount_received: deal.amount_received || 0,
      upsell_credit_amount: deal.upsell_credit_amount || 0,
    });
    
    acc[channel].total++;
    acc[channel].totalValue += effectiveAmount;
    if (deal.stage === 'WON') {
      acc[channel].won++;
      acc[channel].wonValue += effectiveAmount;
    }
    return acc;
  }, {});

  // Convert to arrays sorted by total
  const sourceData: SourceChannelEntry[] = Object.entries(bySource)
    .map(([name, data]) => ({
      name,
      ...data,
      conversionRate: data.total > 0 ? (data.won / data.total) * 100 : 0,
      avgValue: data.total > 0 ? data.totalValue / data.total : 0,
    }))
    .sort((a, b) => b.total - a.total);

  const channelData: SourceChannelEntry[] = Object.entries(byChannel)
    .map(([name, data]) => ({
      name,
      ...data,
      conversionRate: data.total > 0 ? (data.won / data.total) * 100 : 0,
      avgValue: data.total > 0 ? data.totalValue / data.total : 0,
    }))
    .sort((a, b) => b.total - a.total);

  // Count reactivation deals
  const reactivationDeals = deals.filter(d => d.is_reactivation === true || d.deals_source === 'Reactivation');

  return {
    sourceData,
    channelData,
    reactivationDeals,
    totalDeals: deals.length,
  };
}
